import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CONFIG } from '@cpd-app/configuration';
import {
  AccountCreationPromptService,
  ViaNudgesService
} from '@cpd-app/core-module/services';
import { AiChatFacade } from '@cpd-app/state/ai-chat';
import {
  applyViaPanelSize,
  isNarrowViewport,
  viaPanelSize
} from '@cpd-app/state/ai-chat/via-panel-width';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { DeviceFacade } from '@cpd-app/state/device';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { combineLatest, delay, noop, race, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  CPD_ANALYTICS_EVENT,
  GoogleAnalyticsService
} from '@cpd-app/core-module/services/google-analytics/google-analytics-service.service';

/**
 * Via answers in Markdown. A screen reader would read the markup aloud
 * ("hash hash", "star star"), so announce the words only: link text without
 * its URL, no heading, emphasis or code marks.
 */
function toSpokenText(markdown: string): string {
  return (markdown ?? '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_`>]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

@Component({
  selector: 'cpd-chat-via',
  templateUrl: './chat-via.component.html',
  styleUrls: ['./chat-via.component.scss']
})
export class ChatViaComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public isChatViaOpen = false;

  public isFullScreen = false;
  public isShowAllConversations = false;
  public isSidebarExpanded = false;

  /** A11Y.1: where focus was when the user opened Via; it returns there. */
  private opener: HTMLElement | null = null;
  /** A11Y.1: true when Via opened on a timer rather than on request. */
  private openedAutomatically = false;

  public questionForm = new FormGroup({
    message: new FormControl(
      { value: this.viaNudgesService.unSentMessage(), disabled: false },
      [Validators.required]
    )
  });

  private readonly conversationId = toSignal(
    this.aiChatFacade.currentConversationId$
  );
  public messages$ = this.aiChatFacade.messages$;
  public conversations$ = this.aiChatFacade.conversations$;
  public isLoading$ = this.aiChatFacade.isLoading$;
  public isStreaming$ = this.aiChatFacade.isStreaming$;
  public streamingResponse$ = this.aiChatFacade.streamingResponse$;
  public error$ = this.aiChatFacade.error$;
  public currentConversationId$ = this.aiChatFacade.currentConversationId$;
  public conversationsLoading$ = this.aiChatFacade.conversationsLoading$;
  public conversationsCount$ = this.aiChatFacade.conversationsCount$;
  public conversationsLimit$ = this.aiChatFacade.conversationsLimit$;
  public conversationsOffset$ = this.aiChatFacade.conversationsOffset$;
  public rateLimit$ = this.aiChatFacade.rateLimit$;
  public isAuthenticated$ = this.cognitoAuthFacade.isAuthenticated$;

  public get sidebarWidth() {
    return this.isSidebarExpanded
      ? CONFIG.CHAT_VIA.SIDEBAR_EXPANDED_WIDTH
      : CONFIG.CHAT_VIA.SIDEBAR_COLLAPSED_WIDTH;
  }

  constructor(
    private aiChatFacade: AiChatFacade,
    private destroyRef: DestroyRef,
    private userProfileFacade: UserProfileFacade,
    private cognitoAuthFacade: CognitoAuthFacade,
    private deviceFacade: DeviceFacade,
    private accountCreationPromptService: AccountCreationPromptService,
    private viaNudgesService: ViaNudgesService,
    private googleAnalytics: GoogleAnalyticsService,
    private liveAnnouncer: LiveAnnouncer,
    private host: ElementRef<HTMLElement>,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isChatViaOpen'] && this.isChatViaOpen) {
      /*
       * DS-T16 via_opened. Here and not in ngOnInit: this component is an
       * embedded panel whose lifetime is not the same as its visibility, so
       * ngOnInit fires once when the host page builds it, whether or not the
       * learner ever opens Via. The isChatViaOpen transition is the moment
       * somebody actually opened it, and it correctly fires again on a reopen.
       */
      this.googleAnalytics.trackEvent(CPD_ANALYTICS_EVENT.viaOpened);
      this.getRateLimit();
      if (this.openedAutomatically) {
        // A11Y.1 28 Sep: an open nobody asked for (the occupation page's
        // timer) must not move focus (WCAG 2.4.3, 3.2.1). Say it happened.
        void this.liveAnnouncer.announce(
          'Via, your career assistant, opened beside this page.',
          'polite'
        );
        return;
      }
      // A11Y.1: opening Via left focus on the header button, so a keyboard or
      // screen reader user had to hunt for the panel (WCAG 2.4.3). The panel
      // renders under an @if and slides in, hence the wait.
      setTimeout(() => this.focusQuestionBox(), 350);
    } else if (
      changes['isChatViaOpen'] &&
      !changes['isChatViaOpen'].firstChange &&
      changes['isChatViaOpen'].previousValue
    ) {
      // A11Y.1 28 Sep: closing removes the panel, and focus fell to <body>
      // (WCAG 2.4.3). The panel leaves the DOM after this hook, hence the wait.
      setTimeout(() => this.restoreFocusAfterClose());
    }
  }

  public ngOnInit(): void {
    combineLatest([
      this.userProfileFacade.profileWorkType$,
      this.cognitoAuthFacade.isAuthenticated$
    ])
      .pipe(
        filter(([_, isAuthenticate]) => !!isAuthenticate),
        take(1),
        delay(1000)
      )
      .subscribe(() => {
        this.loadConversations();
        this.getRateLimit();
      });

    this.handleErrors();
    this.listenToOpenChatVia();
    this.listenToCloseChatVia();
  }

  public ngOnDestroy(): void {
    this.aiChatFacade.resetAiChatState();
  }

  public onSendMessage(onetCode?: string): void {
    this.cognitoAuthFacade.isAuthenticated$.pipe(take(1)).subscribe({
      next: (isAuthenticated) => {
        const message = this.questionForm.value.message?.trim();

        this.viaNudgesService.setUnSentMessage(message);

        if (isAuthenticated) {
          if (message && this.questionForm.valid) {
            this.questionForm.reset();
            // Disabling the box drops its focus to <body>. Park focus on the
            // panel instead and say what is happening (WCAG 2.4.3, 4.1.3).
            this.focusPanel();
            this.questionForm.controls.message.disable();
            void this.liveAnnouncer.announce('Message sent. Via is thinking.', 'polite');

            this.aiChatFacade.streamStart(message, {
              query: message,
              onetCodes: onetCode && [onetCode]
            });

            /*
             * V-1. The control is re-enabled on EITHER outcome.
             *
             * This subscribed to streamComplete$ alone, which is
             * `ofType(streamComplete)` -- a failure never satisfies it, so the
             * input stayed disabled and the take(1) subscription leaked until
             * the component was destroyed. Only a page reload recovered.
             *
             * streamFailure$ already existed on the facade and was already
             * subscribed elsewhere, but only to hide the "taking longer than
             * usual" notice. Racing the two is what makes the panel usable
             * again after a failure.
             */
            race(
              this.aiChatFacade.streamComplete$,
              this.aiChatFacade.streamFailure$
            )
              .pipe(take(1))
              .subscribe((outcome) => {
                this.questionForm.controls.message.enable();
                // The answer streams into the page silently, so a screen
                // reader user never heard it (WCAG 4.1.3). Read it out once it
                // is complete, then hand focus back to the question box.
                void this.liveAnnouncer.announce(
                  'response' in outcome
                    ? `Via replied. ${toSpokenText(outcome.response)}`
                    : 'Via could not answer that. You can try again.',
                  'polite'
                );
                setTimeout(() => this.focusQuestionBox());
                if (onetCode) {
                  this.viaNudgesService.setCurrentOccupation(null);
                }
              });
            this.viaNudgesService.clearMessage();
          }
        } else {
          this.accountCreationPromptService.openAccountCreationDialog(
            'skills-survey'
          );
        }
      },
      error: noop
    });
  }

  public onToggleSidebar(): void {
    this.isSidebarExpanded = !this.isSidebarExpanded;
    this.updateSize();
  }

  public onToggleFullScreen(): void {
    this.isFullScreen = !this.isFullScreen;
    this.isSidebarExpanded = this.isFullScreen;

    this.updateSize();
  }

  public onNewChat(): void {
    this.isShowAllConversations = false;
    this.viaNudgesService.setCurrentOccupation(null);
    this.aiChatFacade.newChat();
  }

  public onAllConversations(): void {
    this.isShowAllConversations = true;
  }

  public onBack(): void {
    this.isShowAllConversations = false;
  }

  public onConversationSelect(conversationId: number): void {
    this.aiChatFacade.setCurrentConversation(conversationId);
    this.aiChatFacade.getConversationHistory(conversationId);
    this.viaNudgesService.setCurrentOccupation(null);
    this.isShowAllConversations = false;
  }

  public onDeleteConversation(conversationId: number): void {
    this.aiChatFacade.deleteConversation(conversationId);
  }

  public onLoadMore({ offset, limit }): void {
    this.aiChatFacade.loadConversations(offset, limit);
  }

  public onCloseChatVia(): void {
    this.aiChatFacade.closeChatVia();
  }

  /**
   * 29 Sep: Escape closes Via. Via is a side panel, not a modal, and the page
   * beside it has Escape handlers of its own (the Resources guide drawer,
   * typeaheads, the notification drawer), so a page-wide listener would shut
   * Via and that drawer on one key press. Escape is Via's when focus is in
   * the panel, or when nothing has focus (a mouse user who clicked the page).
   * A dialog or menu showing, or a handler that already took the key, wins.
   * Focus then goes back to the opener through the close path above.
   */
  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: KeyboardEvent): void {
    if (!this.isChatViaOpen || event.defaultPrevented) {
      return;
    }
    const target = event.target as Node | null;
    const inPanel = !!target && this.host.nativeElement.contains(target);
    const active = document.activeElement;
    const nothingFocused = !active || active === document.body;
    const overlayShowing = !!document.querySelector(
      '.cdk-overlay-container .cdk-overlay-pane:not(:empty)'
    );
    if (inPanel || (nothingFocused && !overlayShowing)) {
      event.preventDefault();
      this.onCloseChatVia();
    }
  }

  private focusQuestionBox(): void {
    this.host.nativeElement
      .querySelector<HTMLTextAreaElement>('textarea:not(:disabled)')
      ?.focus();
  }

  /**
   * Back to the control that opened Via, or, when that is gone (or Via opened
   * itself), to the Via launcher, then to <main>. Only when focus was lost
   * with the panel: if the user has already moved on, it stays where it is.
   */
  private restoreFocusAfterClose(): void {
    const active = document.activeElement;
    const lost =
      !active ||
      active === document.body ||
      this.host.nativeElement.contains(active);
    const opener = this.opener;
    this.opener = null;
    if (!lost) {
      return;
    }
    const target =
      (opener?.isConnected ? opener : null) ??
      document.querySelector<HTMLElement>(
        'cpd-sidebar button.chat, cpd-via-button button'
      ) ??
      document.querySelector<HTMLElement>('#body-container');
    target?.focus();
  }

  /** Remembers who opened Via, and whether anybody did (see the action). */
  private listenToOpenChatVia(): void {
    this.aiChatFacade.openChatVia$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => {
        if (this.isChatViaOpen) {
          return;
        }
        this.openedAutomatically = action.auto === true;
        const active = document.activeElement as HTMLElement | null;
        this.opener =
          !this.openedAutomatically &&
          active &&
          active !== document.body &&
          !this.host.nativeElement.contains(active)
            ? active
            : null;
      });
  }

  private focusPanel(): void {
    this.host.nativeElement
      .querySelector<HTMLElement>('.chat-via__content')
      ?.focus({ preventScroll: true });
  }

  private resetChatVia(): void {
    this.isFullScreen = false;
    this.isSidebarExpanded = false;
    this.isShowAllConversations = false;
    this.questionForm.reset();
    this.viaNudgesService.setCurrentOccupation(null);
    if (!this.conversationId()) {
      this.loadConversations();
    }
  }

  /**
   * A11Y.1 1.4.10 Reflow: a desktop window narrowed (or zoomed) past the
   * breakpoint kept the 470px panel and scrolled sideways. Re-size on resize,
   * so crossing the breakpoint while Via is open reflows it.
   */
  @HostListener('window:resize')
  public onWindowResize(): void {
    if (this.isChatViaOpen) {
      this.updateSize();
    }
  }

  private updateSize(): void {
    this.deviceFacade.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      const isNarrow = isNarrowViewport();
      if (isMobile && !this.isChatViaOpen) {
        return;
      }
      applyViaPanelSize(
        viaPanelSize({
          open: this.isChatViaOpen,
          isMobile,
          isNarrow,
          isFullScreen: this.isFullScreen,
          sidebarWidth: this.sidebarWidth
        })
      );
    });
  }

  private loadConversations(): void {
    this.aiChatFacade.loadConversations(0, 10);

    this.aiChatFacade.loadConversationsSuccess$
      .pipe(take(1))
      .subscribe(({ conversations }) => {
        if (conversations.length > 0) {
          this.onConversationSelect(conversations[0].id);
          return;
        }

        this.isShowAllConversations = false;
      });
  }

  private handleErrors(): void {
    this.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((error) => {
      if (error) {
        console.error('AI Chat Error:', error);
      }
    });
  }

  private getRateLimit(): void {
    this.aiChatFacade.getRateLimit();
  }

  private listenToCloseChatVia(): void {
    this.aiChatFacade.closeChatVia$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetChatVia();
      });
  }
}
